import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  ModalComponent as DpacModalComponent,
  InputFieldComponent,
  SelectComponent,
  TextareaComponent,
  CheckboxComponent,
  ButtonComponent,
  SnackbarService,
} from '@mic-ac/ui-kit';
import {
  CrmContactService,
  CrmContactPayload,
  CrmRequestReason,
} from '../../core/services/crm-contact/crm-contact.service';

interface ReasonOption {
  /** Valore univoco per il select. */
  id: string;
  /** Slug accettato dal CRM per `lead_requestreason`. */
  slug: CrmRequestReason;
  descrizione: string;
}

@Component({
  selector: 'app-contact-request-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    DpacModalComponent,
    InputFieldComponent,
    SelectComponent,
    TextareaComponent,
    CheckboxComponent,
    ButtonComponent,
  ],
  templateUrl: './contact-request-modal.component.html',
  styleUrl: './contact-request-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactRequestModalComponent {
  readonly #fb = inject(FormBuilder);
  readonly #crm = inject(CrmContactService);
  readonly #snackbar = inject(SnackbarService);

  closed = output<void>();
  submitting = signal(false);

  readonly reasonOptions: ReasonOption[] = [
    {
      id: 'info-generali',
      slug: 'info-generali',
      descrizione: 'Informazioni generali sulla Mappa del Patrimonio',
    },
    {
      id: 'collaborazione',
      slug: 'collaborazione',
      descrizione: 'Collaborazioni e progetti di ricerca',
    },
    {
      id: 'tesi-pubblicazioni',
      slug: 'info-generali',
      descrizione: 'Richiesta di informazioni per tesi, studi o pubblicazioni',
    },
    {
      id: 'eventi-media',
      slug: 'partecipazione-eventi',
      descrizione: 'Eventi, comunicazione, media',
    },
    {
      id: 'segnalazioni',
      slug: 'assistenza-tecnica',
      descrizione: 'Segnalazioni o suggerimenti',
    },
    {
      id: 'altro',
      slug: 'info-generali',
      descrizione: 'Altro',
    },
  ];

  readonly #reasonSlugById = new Map(
    this.reasonOptions.map((option) => [option.id, option.slug]),
  );

  form = this.#fb.group({
    entity: [''],
    email: ['', [Validators.required, Validators.email]],
    name: [''],
    surname: [''],
    requestReason: this.#fb.control<string | null>(null, Validators.required),
    message: ['', [Validators.required, Validators.maxLength(300)]],
    privacy: [false, Validators.requiredTrue],
  });

  onClose(): void {
    this.closed.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const reasonId = raw.requestReason;
    const payload: CrmContactPayload = {
      name: raw.name ?? '',
      surname: raw.surname ?? '',
      email: raw.email ?? '',
      entity: raw.entity ?? '',
      requestReason: this.#reasonSlugById.get(reasonId ?? '') ?? 'info-generali',
      message: raw.message ?? '',
      privacy: raw.privacy ?? false,
    };

    this.submitting.set(true);
    this.#crm.sendLead(payload).subscribe({
      next: (result) => {
        if (result.success) {
          this.onClose();
          void this.#snackbar.show(
            'Richiesta inviata correttamente. Ti risponderemo al più presto.',
            'success',
          );
        } else {
          this.#snackbar.show(
            result.message ?? 'Alcuni campi non sono validi.',
            'warning',
          );
        }
      },
      error: (err) => {
        this.#snackbar.show(
          err?.message ?? "Errore durante l'invio della richiesta.",
          'error',
        );
      },
      complete: () => this.submitting.set(false),
    });
  }
}
